<script>
  export default {
    name: 'MTableHooks',
    data () {
      /**
       * hooks which are called before table rendering started. Used by mixins
       * @type {Array<function(CreateElement)>}
       */
      this.beforeRenderHooks = [];

      /**
       * hooks which are called before header rendering started. Used by mixins
       * @type {Array<function(MTableColumn, number, MTableColumn[])>}
       */
      this.beforeHeaderRenderedHooks = [];

      /**
       * hook which are called when configuring single line header
       * @type {Array<function(Object, Array<VNode>, Boolean)>}
       * @param Object - current column
       * @param Array<MTableColumn> - array of child headers (headers that are below current)
       */
      this.configureFirstLevelHeaderHook = [];

      /**
       * hook which are called when configuring multiline line header
       * @type {Array<function(Object, Number, Array<VNode>, Boolean)>}
       * @param Object - current column
       * @param Array<Object> - array of child headers (headers that are below current)
       * @param Number - index of current `group` (Upper header with child headers)
       */
      this.configureSecondLevelHeadersHook = [];

      /**
       * hooks which are called before cell rendering started. Used by mixins
       * @type {Array<function(Object[], Object, number, number, Array[], MTableColumn[])>}
       */
      this.beforeCellRenderedHooks = [];

      /**
       * hooks which are called after header rendering finished. Used by mixins
       * @type {Array<function(VNode, Object, number, Object[])>}
       */
      this.afterHeaderRenderedHooks = [];

      /**
       * hooks which are called after table rendering finished. Used by mixins
       * @type {Array<function(VNode, CreateElement)>}
       */
      this.afterRenderHooks = [];

      /**
       * hooks which are called after table data is updated
       */
      this.afterDataUpdateHooks = [];

      return {};
    },
    methods: {

      /** HOOKS **/
      /** in order of calls nesting */

      /**
       * call before table rendering started
       * @param h {CreateElement}
       */
      beforeRender (h) {
        this.beforeRenderHooks.forEach(hook => {
          hook(h);
        });
      },

      /**
       * call before header rendering started
       * @param column {Object} - current column
       */
      beforeHeaderRendered (column) {
        this.beforeHeaderRenderedHooks.forEach(hook => {
          hook(column);
        });
      },

      /**
       * call before cell rendering started
       * @param column {Object} - current column
       * @param row {Object[]} - current data row
       * @param i {number} - row index
       * @param j {number} - column index
       * @param data {Array[]} - all data
       * @param columns {Object[]} - all columns
       */
      beforeCellRendered (column, row, i, j, data, columns) {
        this.beforeCellRenderedHooks.forEach(hook => {
          hook(column, row, i, j, data, columns);
        });
      },

      /**
       * call after header rendering finished
       * @param renderedHeader {VNode}
       */
      afterHeaderRendered (renderedHeader) {
        this.afterHeaderRenderedHooks.forEach(hook => {
          hook(renderedHeader);
        });
      },

      /**
       * call after table rendering finished
       * @param renderedTable {VNode}
       * @param h {CreateElement}
       */
      afterRender (renderedTable, h) {
        this.afterRenderHooks.forEach(hook => {
          hook(renderedTable, h);
        });
      },

      afterRowRender (columns, h, tableRow, rowIndex) {
        return this.afterRowRenderProcessors.reduce((acc, hook) => hook(acc, h, tableRow, rowIndex), columns);
      },

      afterDataUpdate () {
        this.afterDataUpdateHooks.forEach(hook => hook());
      },

      /**
       * add {@param hook} into {@link beforeHeaderRenderedHooks}. Used by mixins
       * @param hook {function(MTableColumn, number, MTableColumn[])}
       */
      registerBeforeHeaderRenderedHook (hook) {
        this.beforeHeaderRenderedHooks.push(hook);
      },

      /**
       * add {@param hook} into {@link beforeCellRenderedHooks}. Used by mixins
       * @param hook {function(Object[], MTableColumn, number, number, Array[], MTableColumn[])}
       */
      registerBeforeCellRenderedHook (hook) {
        this.beforeCellRenderedHooks.push(hook);
      },

      /**
       * add {@param hook} into {@link afterHeaderRenderedHooks}. Used by mixins
       * @param hook {function(VNode, MTableColumn, number, MTableColumn[])}
       */
      registerAfterHeaderRenderedHook (hook) {
        this.afterHeaderRenderedHooks.push(hook);
      },

      registerConfigureSecondLevelHeadersHook (hook) {
        this.configureSecondLevelHeadersHook.push(hook);
      }
    }
  };
</script>
